import { Component, inject, Injector, OnInit } from '@angular/core';
import { Database, get, ref } from '@angular/fire/database';
import { AuthService } from 'src/app/services/auth.service';
import { BaseComponent } from 'src/app/shared/baseComponent';
import * as ScenarioDefs from '../vscenarioedit/ScenarioClasses';

interface ModifierMatrixRow {
  className: string;
  counts: number[];
}

@Component({
  selector: 'app-modifier-matrix',
  templateUrl: './modifier-matrix.component.html',
  styleUrls: ['./modifier-matrix.component.css']
})
export class ModifierMatrixComponent extends BaseComponent implements OnInit {
  database = inject(Database);
  isAuthenticatedWithFirebase = false;
  scenarios: any[] = [];
  modifierClasses: string[] = [];
  matrixRows: ModifierMatrixRow[] = [];
  filteredScenarioIndex: number | null = null;

  constructor(injector: Injector, private authService: AuthService) {
    super(injector);
  }

  async ngOnInit(): Promise<void> {
    this.modifierClasses = this.getModifierClasses();
    this.isAuthenticatedWithFirebase = await this.authService.initializeAuth(() => this.onSuccessfulLogin());
  }

  async signInWithGoogle(): Promise<void> {
    this.isAuthenticatedWithFirebase = await this.authService.signInWithGoogle();
    if (this.isAuthenticatedWithFirebase) {
      await this.onSuccessfulLogin();
    }
  }

  private async onSuccessfulLogin(): Promise<void> {
    await this.getData();
  }

  toggleScenarioFilter(scenarioIndex: number): void {
    this.filteredScenarioIndex = this.filteredScenarioIndex === scenarioIndex
      ? null
      : scenarioIndex;
  }

  isRowVisible(row: ModifierMatrixRow): boolean {
    return this.filteredScenarioIndex === null || row.counts[this.filteredScenarioIndex] > 0;
  }

  async getData(): Promise<void> {
    const scenarioRef = ref(this.database, 'scenarios');
    const snapshot = await get(scenarioRef);
    if (!snapshot.exists()) {
      this.scenarios = [];
      this.matrixRows = [];
      return;
    }

    this.scenarios = BaseComponent.toArray(snapshot.val())
      .filter((scenario: any) => scenario.isVersion2 === true)
      .sort((first: any, second: any) => (first.seqNo ?? 0) - (second.seqNo ?? 0));
    const observedClasses = this.scenarios
      .flatMap((scenario: any) => BaseComponent.toArray(scenario.modifiers || []))
      .map((modifier: any) => modifier.class)
      .filter((className: any) => className);
    this.modifierClasses = Array.from(new Set([...this.modifierClasses, ...observedClasses]))
      .sort((first, second) => first.localeCompare(second));
    this.buildMatrixRows();
  }

  private getModifierClasses(): string[] {
    const base = (ScenarioDefs as any).ModifierModel;
    const classes = new Set<string>(['Modifier', 'Grass']);

    for (const [exportName, value] of Object.entries(ScenarioDefs)) {
      if (exportName === 'ModifierModel' || typeof value !== 'function') {
        continue;
      }
      const constructor = value as any;
      if (!base || !(constructor.prototype instanceof base)) {
        continue;
      }

      const model = new constructor();
      const className = model.class === 'Interactivity'
        ? exportName.replace(/Model$/, '')
        : model.class;
      if (className && className !== 'Interactivity') {
        classes.add(className);
      }
    }

    return Array.from(classes).sort((first, second) => first.localeCompare(second));
  }

  private buildMatrixRows(): void {
    const lowSequenceScenarios = this.scenarios.filter((scenario) => Number(scenario.seqNo ?? 0) < 1000);
    const rows = this.modifierClasses.map((className) => ({
      className,
      counts: this.scenarios.map((scenario) => {
        const modifiers = BaseComponent.toArray(scenario.modifiers || []);
        return modifiers.filter((modifier: any) => modifier.class === className).length;
      })
    }));

    this.matrixRows = rows.sort((first, second) => {
      const firstHasLowSequenceOccurrence = lowSequenceScenarios.some((scenario) =>
        BaseComponent.toArray(scenario.modifiers || [])
          .some((modifier: any) => modifier.class === first.className)
      );
      const secondHasLowSequenceOccurrence = lowSequenceScenarios.some((scenario) =>
        BaseComponent.toArray(scenario.modifiers || [])
          .some((modifier: any) => modifier.class === second.className)
      );

      if (firstHasLowSequenceOccurrence !== secondHasLowSequenceOccurrence) {
        return firstHasLowSequenceOccurrence ? -1 : 1;
      }
      return first.className.localeCompare(second.className);
    });
  }
}
